import { message } from 'antd';
import { authAxios } from '../components/utils/session_utils';

const API_BASE_URL = process.env.REACT_APP_BACK_END_API;

export interface AnalyticsValidationRecord {
    id: string;
    key: string;
    value: string;
    ui_label: string;
    created_at?: string;
    updated_at?: string;
    updated_by?: string;
}

export interface CreateValidationRequest {
    key: string;
    value: string;
}

export interface UpdateValidationRequest {
    id: string;
    key: string;
    value: string;
}

class AnalyticsValidationService {
    private async makeRequest(
        endpoint: string,
        options: {
            method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
            data?: any;
            params?: any;
            headers?: any;
        } = {}
    ) {
        try {
            const url = `${API_BASE_URL}/snowflake${endpoint}`;
            const config: any = {
                url,
                method: options.method || 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(options.headers || {}),
                },
            };

            if (options.data) {
                config.data = options.data;
            }
            if (options.params) {
                config.params = options.params;
            }

            const {data} = await authAxios(config);
            return data;
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    async getAllValidations(): Promise<AnalyticsValidationRecord[]> {
        try {
            const data = await this.makeRequest('/analytics-validation');
            return data.rows || [];
        } catch (error) {
            message.error('Failed to fetch validation rules');
            throw error;
        }
    }

    async getValidationById(id: string): Promise<AnalyticsValidationRecord | null> {
        try {
            const data = await this.makeRequest(`/analytics-validation/${id}`);
            return data.rows?.[0] || null;
        } catch (error) {
            message.error('Failed to fetch validation rule');
            throw error;
        }
    }

    async createValidation(validation: CreateValidationRequest): Promise<void> {
        try {
            await this.makeRequest('/analytics-validation', {
                method: 'POST',
                data: JSON.stringify(validation),
            });            
        } catch (error) {
            message.error('Failed to create validation rule');
            throw error;
        }
    }

    async updateValidation(validation: UpdateValidationRequest): Promise<void> {
        try {            
            await this.makeRequest('/analytics-validation', {
                method: 'PUT',
                data: JSON.stringify(validation),
            });
        } catch (error) {
            message.error('Failed to update validation rule');
            throw error;
        }
    }

    async deleteValidation(id: string): Promise<void> {
        try {            
            await this.makeRequest(`/analytics-validation/${id}`, {
                method: 'DELETE',
            });
            message.success('Validation rule deleted successfully');
        } catch (error) {
            message.error('Failed to delete validation rule');
            throw error;
        }
    }

    async deleteBulkValidations(ids: string[]): Promise<void> {
        try {
            await this.makeRequest('/analytics-validation/bulk-delete', {
                method: 'POST',
                data: JSON.stringify({ ids }),
            });
            message.success(`${ids.length} validation rules deleted successfully`);
        } catch (error) {
            message.error('Failed to delete validation rules');
            throw error;
        }
    }
}

export const analyticsValidationService = new AnalyticsValidationService();
export default analyticsValidationService;
